/**
 * Image Upload Button Component
 * Provides image file selection for GPT-5 vision capabilities
 */
import { useRef, ChangeEvent, useState } from 'react';
import { compressImages } from '../utils/imageCompression';
import { toast } from 'react-hot-toast';

interface ImageUploadButtonProps {
  onImagesSelected: (files: File[]) => void;
  disabled?: boolean;
  maxImages?: number;
}

export default function ImageUploadButton({
  onImagesSelected,
  disabled = false,
  maxImages = 5
}: ImageUploadButtonProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isCompressing, setIsCompressing] = useState(false);

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);

    // Filter to only image files
    const imageFiles = files.filter(file =>
      file.type.startsWith('image/')
    );

    // Limit to max images
    const limitedFiles = imageFiles.slice(0, maxImages);

    if (limitedFiles.length > 0) {
      try {
        setIsCompressing(true);

        // Compress images to save bandwidth
        const compressedFiles = await compressImages(limitedFiles, {
          maxWidth: 1920,
          maxHeight: 1080,
          quality: 0.85,
          maxSizeMB: 2
        });

        onImagesSelected(compressedFiles);
      } catch (error) {
        console.error('Image compression error:', error);
        toast.error('Failed to process images');
        // Fallback to original files if compression fails
        onImagesSelected(limitedFiles);
      } finally {
        setIsCompressing(false);
      }
    }

    // Reset input so same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handleFileChange}
        className="hidden"
      />
      <button
        type="button"
        onClick={handleClick}
        disabled={disabled || isCompressing}
        className="p-2 text-dark-text-muted hover:text-dark-text hover:bg-dark-sidebar rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors relative"
        title={isCompressing ? "Compressing images..." : "Take photo or choose from gallery"}
      >
        {isCompressing ? (
          <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        ) : (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2}
            stroke="currentColor"
            className="w-5 h-5"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M6.827 6.175A2.31 2.31 0 015.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 00-1.134-.175 2.31 2.31 0 01-1.64-1.055l-.822-1.316a2.192 2.192 0 00-1.736-1.039 48.774 48.774 0 00-5.232 0 2.192 2.192 0 00-1.736 1.039l-.821 1.316z"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M16.5 12.75a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0zM18.75 10.5h.008v.008h-.008V10.5z"
            />
          </svg>
        )}
      </button>
    </>
  );
}
